import { pick, zip } from 'lodash-es'
import { minutesToMilliseconds } from 'date-fns'
import type { UseQueryOptions } from '@tanstack/vue-query'
import { useQuery } from '@tanstack/vue-query'
import { useQueryParams } from './api-utils'
import type {
  SearchApiParams,
  SearchApiResult,
  SearchApiAccommodation,
  SearchApiSuggestionsResponse,
  SearchApiSuggestion,
} from './search-api.types'

type SearchQueryOptions<R = SearchResult> = Partial<
  Omit<UseQueryOptions<R>, 'select'>
> & {
  enabled?: MaybeRefOrGetter<boolean | undefined>
  select?(result: SearchResult, params: SearchApiParams): R
}

export function useSearchQuery<R = SearchResult>(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: SearchQueryOptions<R> = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()
  const season = useSearchSeason(query)

  const params = computed(() =>
    sortKeys({
      language: context.language,
      currency: context.currency,
      salesoffice: context.salesOfficeCode,
      freeweek: context.isFreeweek || undefined,
      season: season.value,
      ...toValue(query),
    }),
  )

  return useQuery({
    ...(options as Omit<typeof options, 'enabled' | 'select'>),
    placeholderData: (prev: SearchApiResult | undefined) => prev, // Keep previous data between requests
    enabled: computed(
      () => toValue(options.enabled) !== false && import.meta.client,
    ),
    queryKey: ['search-api', 'accomtrips', params] as const,
    queryFn({ queryKey: [_, __, q], signal }) {
      return fetch<SearchApiResult>('/search-api/v1/engines/accomtrips', {
        query: mapSearchApiParams(q),
        signal,
      })
    },
    select(result) {
      const mappedResult = mapSearchResult(context, result, toValue(params), {
        facets: false, // do not map facets for legacy search, since they are mapped in filters
      })

      return options.select
        ? options.select(mappedResult, toValue(query))
        : mappedResult
    },
  })
}

export function useSearchMultiQuery<R = SearchResult[]>(
  requests: MaybeRefOrGetter<SearchApiParams[]>,
  options: {
    enabled?: MaybeRefOrGetter<boolean>
    select?(result: SearchResult[], params: SearchApiParams[]): R
  } = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()

  const params = computed(() => toValue(requests).map(sortKeys))

  return useQuery({
    enabled: toRef(options.enabled),
    queryKey: ['search-api', 'multi', params, context.language] as const,
    queryFn({ queryKey: [_, __, request, language], signal }) {
      return fetch<SearchApiResult[]>(
        '/search-api/v1/engines/accomtrips/multi',
        {
          query: { request: request.map(stringifyParams) },
          headers: removeFalsy({ 'Accept-Language': language }),
          signal,
        },
      )
    },
    select(results) {
      const queries = toValue(requests)
      const mappedResults = results.map((result, index) =>
        mapSearchResult(context, result, queries[index]),
      )

      return options.select
        ? options.select(mappedResults, queries)
        : mappedResults
    },
  })
}

type SearchAlternativeKey = 'sameRegion' | 'extendedDates'

const SEARCH_ALTERNATIVE_COUNT_MAX = 10
const SEARCH_ALTERNATIVE_FUZZINESS = 3

/**
 * Searches for alternatives to a query with zero results.
 *
 * @param query The original search query for which alternatives are to be found.
 */
export function useSearchAlternativesQuery(
  query: MaybeRefOrGetter<SearchApiParams>,
) {
  const context = useSearchContext()
  const season = useSearchSeason(query)

  const requests = computed(() => {
    const queryBase = {
      currency: context.currency,
      salesoffice: context.salesOfficeCode,
      season: season.value,
      ...toValue(query),
      pagesize: SEARCH_ALTERNATIVE_COUNT_MAX,
    }

    return removeFalsy({
      sameRegion: !!queryBase.place && {
        ...queryBase,
        place: undefined,
      },
      extendedDates: hasDateParams(queryBase) && {
        ...queryBase,
        ...addDatesFuzziness(queryBase, SEARCH_ALTERNATIVE_FUZZINESS),
      },
    }) satisfies Record<SearchAlternativeKey, SearchApiParams>
  })

  const params = computed(() => Object.values(requests.value))

  return useSearchMultiQuery(params, {
    /**
     * Disabling search for alternatives for Freeweek.
     *
     * @see https://jira.migros.net/browse/IHGWEBCC-2156
     */
    enabled: () => !context.isFreeweek,
    select(results) {
      return zip(
        Object.keys(requests.value),
        Object.values(requests.value),
        results,
      )
        .filter(([, , result]) => result?.docs.length)
        .slice(0, 1)
        .map(([key, request, result]) => ({
          key: key as SearchAlternativeKey,
          destination: getDestinationForFilter(context.destination, request!),
          request: request!,
          result: result!,
        }))
    },
  })
}

export function useSearchRegionPlacesQuery(
  query: MaybeRefOrGetter<
    Pick<SearchApiParams, 'country' | 'region' | 'facet'>
  >,
  options: { enabled?: MaybeRefOrGetter<boolean | undefined> } = {},
) {
  return useSearchQuery(
    computed(() => ({
      ...pick(toValue(query), ['country', 'region', 'facet']),
      pagesize: SEARCH_API_PAGESIZE_NONE,
    })),
    options,
  )
}

export const SUGGESTIONS_FUZZINESS_DEFAULT = 5
export const SUGGESTIONS_STALE_TIME_MINUTES = 20

export function useSearchApiSuggestionsQuery(query: MaybeRefOrGetter<string>) {
  const fetch = useRequestFetch()

  const confData = useConfdata()
  const settings = useSettings()

  const params = useQueryParams(() => ({
    type: settings.autosuggestSearchtype,
    source: settings.autosuggestSource,
    size: settings.autosuggestSize,
    fuzziness: SUGGESTIONS_FUZZINESS_DEFAULT,
    language: confData.language,
    locale: confData.locale,
    query: toValue(query),
  }))

  const isQueryValid = computed(() => {
    const { length } = toValue(query)

    return !length || length >= settings.autosuggestThreshold
  })

  return useQuery({
    staleTime: minutesToMilliseconds(SUGGESTIONS_STALE_TIME_MINUTES),
    enabled: isQueryValid,
    placeholderData(previousData?: SearchApiSuggestionsResponse) {
      return isQueryValid.value ? previousData : undefined
    },
    queryKey: ['search-suggestions', params] as const,
    queryFn({ queryKey: [_, queryParams], signal }) {
      return fetch<SearchApiSuggestionsResponse>('/search-api/v1/suggest', {
        query: queryParams,
        signal,
      })
    },
    select(data) {
      return flatSuggestions<SearchApiSuggestion>(data?.Suggestions ?? [])
    },
  })
}

/**
 * List accommodations by ids.
 *
 * @see {@link https://webcc-search-test-web-k715.k8s.hotelplan.net/swagger/index.html#/accommodations/ListAccommodations | GET /search-api/v1/accoms}
 */
export function useAccomsQuery<R = SearchApiAccommodation[]>(
  codes: MaybeRefOrGetter<string[]>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const fetch = useRequestFetch()
  const confData = useConfdata()

  const params = useQueryParams(() => ({
    code: toValue(codes),
    language: confData.language,
  }))

  return useQuery({
    queryKey: ['accoms', params] as const,
    ...options,
    placeholderData: keepPreviousDataPlaceholder,
    queryFn({ queryKey: [, query] }) {
      return fetch<R>(`/search-api/v1/accoms`, { query })
    },
  })
}

export function useAccomVacanciesQuery(
  code: MaybeRefOrGetter<string>,
  trip: MaybeRefOrGetter<Partial<TripParams>>,
) {
  const fetch = useRequestFetch()

  const runtimeConfig = useRuntimeConfig()
  const context = useAppContext()
  const isFreeweek = useFlag('freeweek')
  const params = useQueryParams(() => ({
    maxage: runtimeConfig.public.maxage.vacancies,
    currency: context.currency,
    freeweek: isFreeweek.value || undefined,
    // The pax is critical because search-api returns different min price
    // depending on whether it is specified or not.
    pax: toValue(trip).pax,
  }))

  return useQuery({
    queryKey: [`accom-vacancies`, code, params] as const,
    placeholderData: keepPreviousDataPlaceholder,
    queryFn({ queryKey: [_, id, query] }) {
      return fetch<Trips>(`/search-api/v1/accoms/${id}/vacancies`, { query })
    },
  })
}

/**
 * Converts params to a multi-search request string.
 *
 * @param params Request params.
 * @returns String in format `country:AT,pets:true,sorting:-averagerating`.
 */
function stringifyParams(params: object): string {
  return JSON.stringify(params).replace(/["{}]/g, '')
}
